import { EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';
import { ampli } from 'lib-shared-tracking';
import { useEffect } from 'react';

import { useSuggestion } from '../hooks/useSuggestion';

import { SuggestionPreview } from './suggestionPreview';

type SelectedSuggestionPreviewProps = {
  suggestionId: string;
  /** Approved or discarded — the draft is gone and nothing should stay selected. */
  onRemoved: () => void;
};

/**
 * Resolves the selected draft and renders the preview for it.
 *
 * The lookup lives here rather than in the page so that `useSuggestion` runs
 * against an id that is known to be set, and rather than in the preview so the
 * preview stays a plain function of the draft it is given.
 *
 * A draft that cannot be read gets a message and a retry rather than the blank
 * pane an absent draft leaves: the row stays selected either way, so with
 * nothing said the reviewer cannot tell a failure from an empty preview.
 */
export function SelectedSuggestionPreview({
  suggestionId,
  onRemoved,
}: SelectedSuggestionPreviewProps): JSX.Element | null {
  const { suggestion, loading, failed, retry } = useSuggestion(suggestionId);
  const isLoaded = !!suggestion;

  // Once per draft opened: the page remounts this per selected id.
  useEffect(() => {
    if (isLoaded) ampli.knowledgeArticleSuggestionRead();
  }, [isLoaded]);

  if (!suggestion) {
    if (loading) return <KnowledgeLoading />;

    if (failed) {
      return (
        <FlexBox
          flexDirection="column"
          justifyContent="center"
          flex="1"
          height="100%"
          background="neutral.000"
        >
          <EmptyState
            size="small"
            icon="exclamation-triangle-filled"
            title="This suggestion could not be loaded"
            description="Something went wrong reading the suggestion. Try again, or pick another one from the list."
            buttonProps={{ children: 'Try again', onClick: retry }}
          />
        </FlexBox>
      );
    }

    return null;
  }

  return <SuggestionPreview suggestion={suggestion} onRemoved={onRemoved} />;
}
